import { Effects } from './effects';
import { arcPosition, clamp, randomBetween } from './math';
import { PageAdapter } from './page';
import { Renderer } from './renderer';
import { Timeline } from './timeline';
import { clonePositions } from './clones';
import { projectileAnchor } from './sprite';
import { Routines } from './routines';
import { PointerReaction } from './pointer';
import { pickAutonomousAction } from './autoplay';
import { pickArrival, placeArrival } from './arrival';
import type { Arrival } from './arrival';
import type { ThemeBehavior } from './behavior';
import type {
  Action,
  Actor,
  AnimateEvent,
  AnimateInstance,
  AnimateOptions,
  AnimateSnapshot,
  Pose,
  Surface,
  Theme,
  Motion,
  Point,
} from './types';

/** Owns simulation time and actor state. Rendering and host-page effects are adapters. */
export class AnimateEngine implements AnimateInstance {
  readonly ready: Promise<void>;
  private page: PageAdapter;
  private renderer: Renderer;
  private effects: Effects;
  private routines: Routines;
  private behavior: ThemeBehavior | undefined;
  private drawOverlay = (ctx: CanvasRenderingContext2D) => this.behavior?.draw?.(ctx);
  private pointerReaction = new PointerReaction();
  private pointer: Point | undefined;
  private timeline = new Timeline();
  private abort = new AbortController();
  private media = window.matchMedia('(prefers-reduced-motion: reduce)');
  private actors: Actor[] = [];
  private nextId = 1;
  private time = 0;
  private nextAction = 5;
  private lastAction: Action | undefined;
  private lastTime = 0;
  private raf = 0;
  private lastDust = 0;
  private loaded = false;
  private destroyed = false;
  private busy = false;
  private paused: boolean;
  private readonly arrival: Arrival;
  private speech: string;
  private speechUntil = 5;
  private size: number;
  private maxClones: number;
  private config: {
    autoplay: boolean;
    speech: boolean;
    interactWithPage: boolean;
    activityInterval: number;
  };

  constructor(
    private theme: Theme,
    private options: AnimateOptions,
  ) {
    this.effects = new Effects(theme.drawProjectile);
    this.size = clamp(Number.isFinite(options.size) ? options.size! : 150, 80, 240);
    this.maxClones = Math.floor(
      clamp(Number.isFinite(options.maxClones) ? options.maxClones! : 3, 0, 6),
    );
    this.config = {
      autoplay: options.autoplay ?? true,
      speech: options.speech ?? true,
      interactWithPage: options.interactWithPage ?? true,
      activityInterval: clamp(
        Number.isFinite(options.activityInterval) ? options.activityInterval! : 7,
        3,
        60,
      ),
    };
    this.paused = this.media.matches;
    this.page = new PageAdapter(options.container);
    this.renderer = new Renderer(theme, (id) => this.clickActor(id));
    this.arrival = pickArrival(theme);
    this.resetActor();
    this.speech = this.arrival.message;
    this.behavior = theme.createBehavior?.({
      hero: () => this.hero,
      page: this.page,
      effects: this.effects,
      floor: () => this.floor,
      fitX: (x) => this.fitX(x),
      interacts: () => this.config.interactWithPage,
      reducedMotion: () => this.media.matches,
      pose: (pose) => this.setPose(this.hero, pose),
      move: (x, y, jumping, surface) => this.moveTo(this.hero, x, y, jumping, surface),
      say: (message, duration = 2.8) => {
        this.speech = message;
        this.speechUntil = this.time + duration;
      },
      complete: () => {
        this.busy = false;
      },
    });
    this.routines = new Routines(
      {
        hero: () => this.hero,
        floor: () => this.floor,
        interacts: () => this.config.interactWithPage,
        fitX: (x) => this.fitX(x),
        pose: (pose) => this.setPose(this.hero, pose),
        move: (x, y, jumping, surface) => this.moveTo(this.hero, x, y, jumping, surface),
        complete: () => {
          this.busy = false;
        },
      },
      this.page,
      this.effects,
    );
    const signal = this.abort.signal;
    window.addEventListener(
      'pointermove',
      (event) => {
        this.pointer =
          event.pointerType === 'mouse' && !event.buttons
            ? { x: event.clientX, y: event.clientY }
            : undefined;
      },
      { signal, passive: true },
    );
    window.addEventListener(
      'pointerout',
      (event) => {
        if (!event.relatedTarget) this.pointer = undefined;
      },
      { signal, passive: true },
    );
    window.addEventListener(
      'blur',
      () => {
        this.pointer = undefined;
      },
      { signal },
    );
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) {
          this.stopLoop();
          this.page.pause();
        } else if (!this.paused) {
          this.page.resume();
          this.startLoop();
        }
      },
      { signal },
    );
    this.media.addEventListener(
      'change',
      () => {
        if (this.media.matches) this.pause();
      },
      { signal },
    );
    const syncGeometry = () => {
      // Scroll runs before paint. A throttled fixed overlay visibly trails the document.
      if (this.loaded) {
        this.reflow();
        this.draw();
      }
    };
    window.addEventListener('resize', syncGeometry, { signal, passive: true });
    window.addEventListener('scroll', syncGeometry, { signal, passive: true, capture: true });
    const assetBase = new URL(options.assetBase ?? `/themes/${this.theme.id}/`, document.baseURI)
      .href;
    this.ready = this.renderer
      .load(assetBase)
      .then(() => {
        if (this.destroyed) return;
        this.loaded = true;
        this.reflow();
        // Asset loading may span a resize: resolve the saved arrival in the latest bounds.
        this.resetActor();
        this.draw();
        this.startLoop();
        this.emit({
          type: 'ready',
          message: this.paused
            ? 'Animations en pause : mouvement réduit activé.'
            : `${this.theme.name} est prêt à explorer.`,
        });
      })
      .catch((error: unknown) => {
        if (!this.destroyed) {
          this.emit({
            type: 'error',
            message: error instanceof Error ? error.message : 'Chargement du thème impossible.',
          });
          this.destroy();
        }
        throw error;
      });
    // A consumer may choose events instead of awaiting ready; still expose its rejection.
    void this.ready.catch(() => {});
  }

  private get hero(): Actor {
    return this.actors[0]!;
  }
  private get floor(): number {
    return Math.max(40, this.page.bounds.height - 14);
  }
  private get canClone(): boolean {
    return (
      this.theme.primaryAction === 'clone' &&
      this.loaded &&
      !this.destroyed &&
      !this.paused &&
      !this.busy &&
      !this.hero.motion?.arc &&
      this.maxClones > 0 &&
      this.actors.length === 1
    );
  }

  private canPerform(action: Action): boolean {
    if (this.destroyed || !this.loaded || this.paused || document.hidden) return false;
    if (typeof this.theme.messages[action] !== 'string') return false;
    const interrupt = this.behavior?.canInterrupt(action) ?? false;
    if ((this.busy || (this.hero.motion?.arc ?? 0) > 0) && !interrupt) return false;
    if (action === 'clone') return this.canClone && this.availableClonePositions().length > 0;
    if (action === 'wallrun') return this.routines.canWallRun;
    return true;
  }
  private emit(event: AnimateEvent): void {
    try {
      this.options.onEvent?.(event);
    } catch (error) {
      console.error('[Animate] onEvent callback failed:', error);
    }
  }
  private setPose(actor: Actor, pose: Pose): void {
    actor.pose = pose;
    actor.poseTime = 0;
  }
  private resetActor(): void {
    this.actors = [
      {
        id: 0,
        clone: false,
        ...placeArrival(this.arrival.position, this.page.bounds.width, this.size),
        y: this.floor,
        size: this.size,
        pose: 'idle',
        poseTime: 0,
        age: 0,
        opacity: 1,
      },
    ];
  }

  trigger(action: Action): boolean {
    if (action === 'wallrun' && !this.destroyed) this.page.refresh();
    if (!this.canPerform(action)) return false;
    if (this.busy && this.behavior?.canInterrupt(action)) this.behavior.cancel();
    this.hero.motion = undefined;
    this.lastAction = action;
    this.speech = this.theme.messages[action]!;
    this.speechUntil = this.time + 2.8;
    this.nextAction = this.time + this.config.activityInterval + randomBetween(0, 3);
    this.emit({ type: 'action', action, message: this.speech });
    switch (action) {
      case 'clone':
        this.clone();
        break;
      case 'rasenshuriken':
        this.attack();
        break;
      case 'summon':
        this.summon();
        break;
      case 'jump':
        this.jump();
        break;
      case 'run':
        this.run();
        break;
      case 'kawarimi':
      case 'dodge':
      case 'ramen':
      case 'wallrun':
      case 'sexyjutsu':
        this.page.refresh();
        this.busy = true;
        this.routines.start(action, this.localPointer());
        break;
      default:
        this.page.refresh();
        this.busy = true;
        this.behavior!.trigger(action);
    }
    return true;
  }

  private clickActor(id: number): void {
    if (this.destroyed || this.paused || !this.loaded) return;
    if (id === 0) {
      this.trigger(this.theme.primaryAction);
      return;
    }
    this.removeClone(id);
  }

  private clone(): void {
    this.busy = true;
    this.setPose(this.hero, 'seal');
    this.timeline.after(0.42, () => {
      const support = this.page.measureSurface(this.hero.surface);
      for (const x of this.availableClonePositions()) {
        const surface =
          support &&
          x >= support.x + this.size * 0.27 &&
          x <= support.x + support.width - this.size * 0.27
            ? support
            : undefined;
        const actor: Actor = {
          ...this.hero,
          id: this.nextId++,
          clone: true,
          x,
          y: surface?.y ?? this.floor,
          surface: surface?.element,
          pose: 'idle',
          poseTime: 0,
          age: 0,
          motion: undefined,
          expiresAt: this.time + randomBetween(13, 19),
          opacity: 1,
        };
        this.actors.push(actor);
        this.effects.smoke(actor.x, actor.y, this.size / 150);
      }
    });
    this.timeline.after(0.9, () => {
      this.busy = false;
      this.setPose(this.hero, 'idle');
    });
  }

  private availableClonePositions(): number[] {
    return clonePositions(
      this.hero.x,
      this.size,
      this.page.bounds.width,
      Math.min(2, this.maxClones),
    );
  }

  private removeClone(id: number): void {
    const actor = this.actors.find((candidate) => candidate.id === id && candidate.clone);
    if (!actor) return;
    this.effects.smoke(actor.x, actor.y, this.size / 150);
    this.actors = this.actors.filter((candidate) => candidate.id !== id);
    this.emit({ type: 'clone-removed', message: 'Pouf ! Le clone a disparu.' });
  }

  private attack(): void {
    this.busy = true;
    this.setPose(this.hero, 'charge');
    this.page.refresh();
    const surfaces = this.config.interactWithPage ? this.page.surfaces : [];
    const target = surfaces[Math.floor(Math.random() * surfaces.length)];
    const targetPoint = target
      ? { x: clamp(target.x + target.width / 2, 30, this.page.bounds.width - 30), y: target.y + 28 }
      : {
          x:
            this.hero.x > this.page.bounds.width / 2
              ? this.page.bounds.width * 0.22
              : this.page.bounds.width * 0.78,
          y: this.floor - this.size * 0.8,
        };
    this.hero.facing = targetPoint.x > this.hero.x ? 1 : -1;
    this.effects.charge = {
      ...projectileAnchor(this.hero, this.theme),
      radius: this.size * 0.065,
      time: 0,
      formation: 0,
    };
    this.timeline.after(1.15, () => {
      this.syncCharge();
      const held = this.effects.charge!;
      const from = { x: held.x, y: held.y };
      const currentTarget = this.page.measureSurface(target?.element);
      if (currentTarget) {
        targetPoint.x = clamp(
          currentTarget.x + currentTarget.width / 2,
          30,
          this.page.bounds.width - 30,
        );
        targetPoint.y = currentTarget.y + 28;
      }
      const direction = this.hero.facing;
      this.effects.charge = undefined;
      this.setPose(this.hero, 'throw');
      this.effects.launch(
        from,
        targetPoint,
        () => {
          if (target && this.config.interactWithPage && !this.media.matches)
            this.page.impact(target.element, direction);
          this.emit({
            type: 'impact',
            message:
              target && this.config.interactWithPage
                ? 'Impact ! Le décor reprend sa place.'
                : 'Rasenshuriken lancé !',
          });
        },
        { radius: held.radius, rotation: held.time * 22 },
      );
    });
    this.timeline.after(1.65, () => {
      this.busy = false;
      this.setPose(this.hero, 'idle');
    });
  }

  private summon(): void {
    this.busy = true;
    this.setPose(this.hero, 'seal');
    this.timeline.after(0.45, () => {
      this.setPose(this.hero, 'summon');
    });
    this.timeline.after(0.75, () => {
      if (this.effects.summons.length >= 2) this.effects.summons.shift();
      const x = this.fitX(
        this.hero.x + (this.hero.x > this.page.bounds.width / 2 ? -1 : 1) * this.size * 0.9,
      );
      this.effects.summon(x, this.hero.y, this.size * 1.15);
    });
    this.timeline.after(1.25, () => {
      this.busy = false;
      this.setPose(this.hero, 'idle');
    });
  }

  private fitX(x: number): number {
    return clamp(x, this.size * 0.4, this.page.bounds.width - this.size * 0.4);
  }

  private jump(): void {
    this.page.refresh();
    const surfaces = this.config.interactWithPage
      ? this.page.surfaces.filter(
          (surface) => surface.element !== this.hero.surface && surface.y > this.size * 0.85,
        )
      : [];
    const target = surfaces[Math.floor(Math.random() * surfaces.length)];
    const x = target
      ? this.fitX(target.x + target.width * randomBetween(0.3, 0.7))
      : this.fitX(this.hero.x + randomBetween(-240, 240));
    const y = target?.y ?? this.floor;
    this.moveTo(this.hero, x, y, true, target);
  }

  private run(): void {
    const surface = this.page.measureSurface(this.hero.surface);
    const min = surface ? this.fitX(surface.x + 24) : this.size * 0.45;
    const max = surface
      ? this.fitX(surface.x + surface.width - 24)
      : this.page.bounds.width - this.size * 0.45;
    let targetX = randomBetween(min, Math.max(min, max));
    if (Math.abs(targetX - this.hero.x) < 60) targetX = this.hero.x > (min + max) / 2 ? min : max;
    this.moveTo(this.hero, targetX, this.hero.y, false, surface);
  }

  private moveTo(actor: Actor, x: number, y: number, jumping: boolean, surface?: Surface): Motion {
    actor.facing = x >= actor.x ? 1 : -1;
    const distance = Math.hypot(x - actor.x, y - actor.y);
    const origin = this.page.measureSurface(actor.surface);
    actor.motion = {
      from: { x: actor.x, y: actor.y },
      to: { x, y },
      duration: jumping ? clamp(distance / 380, 0.6, 1.3) : Math.max(0.25, distance / 140),
      elapsed: 0,
      arc: jumping ? Math.min(115, Math.max(50, Math.min(y, actor.y) - this.size)) : 0,
      surface: surface?.element,
      origin: origin
        ? { element: origin.element, offsetX: actor.x - origin.x, offsetY: actor.surfaceOffsetY }
        : undefined,
      targetOffsetX: surface ? x - surface.x : undefined,
    };
    actor.surface = jumping ? undefined : surface?.element;
    actor.surfaceOffsetY = 0;
    this.setPose(actor, jumping ? 'jump' : 'run');
    return actor.motion;
  }

  private reflow(): void {
    this.page.refresh();
    this.renderer.resize(this.page.bounds);
    for (const actor of this.actors) {
      actor.x = this.fitX(actor.x);
      if (actor.motion) {
        const motion = actor.motion;
        const origin = this.page.measureSurface(motion.origin?.element);
        if (origin && motion.origin) {
          motion.from.x = origin.x + motion.origin.offsetX;
          motion.from.y = origin.y + (motion.origin.offsetY ?? 0);
        }
        const destination = this.page.measureSurface(motion.surface);
        if (destination) {
          motion.to.y = destination.y + (motion.targetOffsetY ?? 0);
          const x = destination.x + (motion.targetOffsetX ?? destination.width / 2);
          motion.to.x =
            motion.clampToSurface === false
              ? x
              : clamp(x, destination.x + 15, destination.x + destination.width - 15);
        } else if (motion.surface) {
          motion.surface = undefined;
          motion.to.y = this.floor;
        }
        const wall = this.page.measureSurface(motion.wall?.element);
        if (wall && motion.wall) {
          motion.to.x = motion.wall.side === 'left' ? wall.x : wall.x + wall.width;
          motion.to.y = motion.wall.phase === 'approach' ? wall.y + wall.height - 8 : wall.y + 12;
          if (motion.wall.phase === 'climb') {
            motion.from.x = motion.to.x;
            motion.from.y = wall.y + wall.height - 8;
          }
        }
        motion.to.x = this.fitX(motion.to.x);
        if (!motion.surface && !motion.wall) motion.to.y = Math.min(motion.to.y, this.floor);
        Object.assign(
          actor,
          arcPosition(motion.from, motion.to, motion.elapsed / motion.duration, motion.arc),
        );
        continue;
      }
      const surface = this.page.measureSurface(actor.surface);
      if (surface && this.config.interactWithPage) {
        actor.y = surface.y + (actor.surfaceOffsetY ?? 0);
        actor.x = this.fitX(clamp(actor.x, surface.x + 15, surface.x + surface.width - 15));
      } else {
        actor.surface = undefined;
        actor.y = this.floor;
      }
    }
    for (const log of this.effects.logs) {
      const surface = this.page.measureSurface(log.surface);
      if (surface && this.config.interactWithPage) {
        log.x = surface.x + log.offsetX;
        log.y = surface.y;
      } else {
        log.surface = undefined;
        log.y = this.floor;
      }
    }
  }

  private tick = (timestamp: number): void => {
    this.raf = 0;
    if (this.paused || this.destroyed || document.hidden || !this.loaded) return;
    const dt = Math.min((timestamp - (this.lastTime || timestamp)) / 1000, 0.05);
    this.lastTime = timestamp;
    this.time += dt;
    if (this.page.dirty) {
      this.reflow();
    }
    this.timeline.update(dt);
    this.routines.update(dt);
    this.behavior?.update(dt, this.localPointer());
    for (const actor of [...this.actors]) {
      actor.age += dt;
      actor.poseTime += dt;
      if (actor.expiresAt !== undefined && this.time >= actor.expiresAt) {
        this.removeClone(actor.id);
        continue;
      }
      if (actor.motion) {
        const motion = actor.motion;
        motion.elapsed += dt;
        Object.assign(
          actor,
          arcPosition(motion.from, motion.to, motion.elapsed / motion.duration, motion.arc),
        );
        if (motion.elapsed >= motion.duration) {
          actor.surface = motion.surface;
          actor.surfaceOffsetY = motion.targetOffsetY ?? 0;
          actor.motion = undefined;
          this.setPose(actor, motion.arc ? 'land' : 'idle');
          if (motion.arc && !motion.quietLanding) this.effects.smoke(actor.x, actor.y, 0.32);
          motion.onComplete?.();
        } else if (actor.pose === 'run' && this.time - this.lastDust > 0.09) {
          this.effects.dust(actor.x - actor.facing * 22, actor.y);
          this.lastDust = this.time;
        }
      } else if (actor.pose === 'land' && actor.poseTime > 0.18) this.setPose(actor, 'idle');
    }
    this.effects.update(dt);
    if (
      this.pointerReaction.update(
        this.time,
        this.hero,
        this.localPointer(),
        this.config.autoplay && !this.media.matches && !this.busy && !this.hero.motion,
      )
    )
      this.trigger(this.theme.pointerAction);
    if (
      this.config.autoplay &&
      !this.media.matches &&
      !this.busy &&
      !this.hero.motion &&
      this.time >= this.nextAction
    ) {
      this.trigger(
        pickAutonomousAction(
          this.lastAction,
          {
            wallrun: !!this.theme.messages.wallrun && this.routines.canWallRun,
            clone: this.canClone && this.availableClonePositions().length > 0,
          },
          Math.random,
          this.theme.autoplay,
        ),
      );
    }
    this.draw();
    this.raf = requestAnimationFrame(this.tick);
  };

  private localPointer(): Point | undefined {
    if (!this.pointer) return undefined;
    const { left, top, width, height } = this.page.bounds;
    const point = { x: this.pointer.x - left, y: this.pointer.y - top };
    return point.x >= 0 && point.x <= width && point.y >= 0 && point.y <= height
      ? point
      : undefined;
  }

  private draw(): void {
    if (this.loaded) {
      this.behavior?.reflow();
      this.syncCharge();
      this.renderer.draw(
        this.actors,
        this.effects,
        this.canPerform(this.theme.primaryAction),
        this.config.speech && this.time < this.speechUntil ? this.speech : undefined,
        this.drawOverlay,
      );
    }
  }
  private syncCharge(): void {
    if (!this.effects.charge) return;
    const progress = clamp(this.hero.poseTime / 1.15, 0, 1);
    Object.assign(this.effects.charge, projectileAnchor(this.hero, this.theme), {
      radius: this.size * (0.065 + 0.15 * progress),
      formation: clamp((this.hero.poseTime - 0.5) / 0.35, 0, 1),
    });
  }
  private startLoop(): void {
    if (this.destroyed || this.paused || document.hidden || !this.loaded || this.raf) return;
    this.lastTime = 0;
    this.raf = requestAnimationFrame(this.tick);
  }
  private stopLoop(): void {
    cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.lastTime = 0;
  }

  pause(): void {
    if (this.destroyed || this.paused) return;
    this.paused = true;
    this.stopLoop();
    this.page.pause();
    this.draw();
    this.emit({ type: 'pause', message: `${this.theme.name} fait une pause.` });
  }
  resume(): void {
    if (this.destroyed || !this.paused) return;
    this.paused = false;
    this.reflow();
    this.page.resume();
    this.draw();
    this.startLoop();
    this.emit({ type: 'resume', message: 'C’est reparti !' });
  }
  configure(
    options: Partial<
      Pick<AnimateOptions, 'autoplay' | 'speech' | 'interactWithPage' | 'activityInterval'>
    >,
  ): void {
    if (this.destroyed) return;
    if (typeof options.autoplay === 'boolean') this.config.autoplay = options.autoplay;
    if (typeof options.speech === 'boolean') this.config.speech = options.speech;
    if (typeof options.interactWithPage === 'boolean') {
      this.config.interactWithPage = options.interactWithPage;
      if (!options.interactWithPage) {
        this.routines.cancel();
        this.behavior?.cancel();
        this.page.restore();
        this.effects.logs = [];
        for (const actor of this.actors) {
          actor.motion = undefined;
          actor.surface = undefined;
          actor.surfaceOffsetY = 0;
          actor.occlusion = undefined;
          actor.y = this.floor;
        }
        if (!this.busy) this.setPose(this.hero, 'idle');
      }
    }
    if (Number.isFinite(options.activityInterval))
      this.config.activityInterval = clamp(options.activityInterval!, 3, 60);
    // A visual-only speech toggle must not postpone the next autonomous action.
    if (
      typeof options.autoplay === 'boolean' ||
      typeof options.interactWithPage === 'boolean' ||
      Number.isFinite(options.activityInterval)
    )
      this.nextAction = this.time + this.config.activityInterval;
    this.draw();
  }
  getSnapshot(): AnimateSnapshot {
    return {
      theme: this.theme.id,
      availableActions: (Object.keys(this.theme.messages) as Action[]).filter((action) =>
        this.canPerform(action),
      ),
      paused: this.paused,
      ...this.config,
      clones: this.actors.length - 1,
      canClone: this.canClone,
      canWallRun:
        !!this.theme.messages.wallrun &&
        this.loaded &&
        !this.destroyed &&
        !this.paused &&
        !this.busy &&
        !this.hero.motion?.arc &&
        this.routines.canWallRun,
      busy: this.busy,
      reducedMotion: this.media.matches,
      state: this.hero.pose,
    };
  }
  reset(): void {
    if (this.destroyed) return;
    this.timeline.clear();
    this.routines.cancel();
    this.behavior?.cancel();
    this.pointerReaction.reset();
    this.pointer = undefined;
    this.effects.clear();
    this.page.restore();
    this.time = 0;
    this.nextAction = 5;
    this.lastAction = undefined;
    this.lastDust = 0;
    this.busy = false;
    this.speech = this.arrival.message;
    this.speechUntil = 5;
    this.reflow();
    this.resetActor();
    this.draw();
  }
  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.stopLoop();
    this.abort.abort();
    this.timeline.clear();
    this.routines.cancel();
    this.behavior?.cancel();
    this.effects.clear();
    this.page.destroy();
    this.renderer.destroy();
  }
}
